import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { ArrowRight, Sparkle, Star, Sun, Moon, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const DEITY_ROLE_TINT = {
  ishta: { bg: "from-amber-50 to-yellow-100", border: "border-amber-300", accent: "text-amber-900", badge: "bg-amber-300 text-amber-950" },
  pala: { bg: "from-emerald-50 to-teal-50", border: "border-emerald-200", accent: "text-emerald-900", badge: "bg-emerald-200 text-emerald-900" },
  kula: { bg: "from-violet-50 to-fuchsia-50", border: "border-violet-200", accent: "text-violet-900", badge: "bg-violet-200 text-violet-900" },
};

function DevataCard({ role, title, sub, dev }) {
  const tint = DEITY_ROLE_TINT[role] || DEITY_ROLE_TINT.ishta;
  return (
    <article className={`rounded-2xl overflow-hidden border ${tint.border}`} data-testid={`devata-${role}`}>
      <div className={`p-6 bg-gradient-to-br ${tint.bg}`}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${tint.badge}`}>{title}</span>
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-soft text-[#3F2D1B]">{sub}</span>
        </div>
        <h3 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light text-[#3F2D1B]">{dev.deity.primary}</h3>
        <p className={`text-xs mt-2 italic ${tint.accent}`}>{dev.via}</p>
        <p className="text-sm text-[#3F2D1B] mt-4 font-light leading-snug">{dev.deity.blurb}</p>
      </div>
      <div className="p-5 bg-white space-y-3 text-sm">
        <div>
          <div className="label-eyebrow text-muted">Primary mantra</div>
          <p className="font-medium text-[#3F2D1B] mt-1 leading-snug">{dev.deity.mantra}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <div className="label-eyebrow text-muted">Best day</div>
            <p className="font-medium text-[#3F2D1B] mt-1">{dev.deity.day}</p>
          </div>
          <div>
            <div className="label-eyebrow text-muted">Color</div>
            <p className="font-medium text-[#3F2D1B] mt-1 capitalize">{dev.deity.color}</p>
          </div>
          <div>
            <div className="label-eyebrow text-muted">Indicator</div>
            <p className="font-medium text-[#3F2D1B] mt-1">{dev.indicator_sign} <span className="text-muted">via {dev.indicator_planet}</span></p>
          </div>
        </div>
        <div>
          <div className="label-eyebrow text-muted">Offerings & rituals</div>
          <p className="text-[#3F2D1B] mt-1 font-light leading-snug">{dev.deity.offering}</p>
        </div>
        {dev.deity.alternatives?.length > 0 && (
          <div>
            <div className="label-eyebrow text-muted">Alternative forms you may worship</div>
            <p className="text-[#3F2D1B] mt-1 font-light leading-snug">{dev.deity.alternatives.join(" · ")}</p>
          </div>
        )}
        {dev.other_planets_in_sign?.length > 0 && (
          <div className="text-xs text-muted">
            Other planets in this indicator sign: {dev.other_planets_in_sign.join(", ")} — their deities also support this worship.
          </div>
        )}
      </div>
    </article>
  );
}

export default function IshtaDevataPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/ishta-devata/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='ishta-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute Ishta Devata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Crown size={14} weight="duotone" /> Ishta Devata · main worshipping deity
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Your <span className="text-gold">main deity</span> to worship
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Classical Jaimini method — your <b>Atmakaraka</b> (the planet with highest degree-in-sign in D1) lands in a specific sign in your D9 chart. That sign becomes your <b>Karakamsa Lagna</b>. The <b>12th house FROM the Karakamsa</b> in D9 reveals your <b>Ishta Devata</b> — the deity who liberates your soul. Your D1's 12th house (moksha bhava) cross-references the same. We also surface your <b>Pala Devata</b> (6th from Karakamsa — daily protector) and <b>Kula Devata</b> (9th from Karakamsa — family/dharma deity).
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="ishta-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="ishta-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="ishta-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="ishta-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`ishta-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="ishta-submit">
            {loading ? "Finding your deity…" : <>Reveal my Ishta Devata <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="ishta-result">
            {/* === KARAKAMSA + AK SUMMARY === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="karakamsa-summary">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Star size={14} weight="duotone" /> Step 1 · Your soul's seat
              </div>
              <h2 className="font-serif-display text-3xl mt-2 font-light text-[#3F2D1B]">
                Atmakaraka: <span className="text-gold">{data.atmakaraka.planet}</span> <span className="text-muted text-base font-light">at {data.atmakaraka.deg.toFixed(2)}° in {data.atmakaraka.rashi}</span>
              </h2>
              <p className="text-sm text-[#3F2D1B] mt-3 font-light">
                In your D9 (Navamsa), <b>{data.atmakaraka.planet}</b> lands in <b>{data.karakamsa.rashi}</b> — this is your <b>Karakamsa Lagna</b>. Every devata indicator is counted from here.
              </p>
            </section>

            {/* === PRIMARY ISHTA DEVATA (12th from Karakamsa) === */}
            <PaywallGate
              tool="ishta_devata"
              toolLabel="Ishta Devata · Your Soul's Deity"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-10">
              <DevataCard
                role="ishta"
                title="ISHTA DEVATA · the moksha deity"
                sub={`12th from Karakamsa · ${data.ishta_devata.indicator_sign}`}
                dev={data.ishta_devata}
              />

            {/* === D1 12TH HOUSE CROSS-REFERENCE === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#F5EDFE] to-[#E9DDFB] border border-violet-200" data-testid="d1-cross-ref">
              <div className="label-eyebrow text-violet-900 flex items-center gap-2">
                <Moon size={14} weight="duotone" /> D1 12th house — moksha bhava cross-reference
              </div>
              <h3 className="font-serif-display text-2xl mt-2 font-light text-[#3F2D1B]">
                {data.d1_twelfth.rashi} <span className="text-muted text-base font-light">(lord: {data.d1_twelfth.lord})</span>
                {data.d1_twelfth.planets.length > 0 && (
                  <span className="text-muted text-base font-light"> · planets: {data.d1_twelfth.planets.join(", ")}</span>
                )}
              </h3>
              <p className="text-sm text-[#3F2D1B] mt-3 font-light leading-snug">{data.d1_twelfth.note}</p>
              {data.cross_reference?.echoes?.length > 0 ? (
                <div className="mt-4 space-y-2">
                  <div className="label-eyebrow text-violet-900">Resonance with your Ishta indicator</div>
                  <ul className="space-y-2 text-sm">
                    {data.cross_reference.echoes.map((e, i) => (
                      <li key={i} className="flex gap-3 leading-snug">
                        <Sparkle size={14} weight="duotone" className="text-violet-700 flex-shrink-0 mt-1" />
                        <span className="text-[#3F2D1B]">{e}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted mt-3 italic">
                  D1's 12th house and the Karakamsa-12th indicator are different signs — your moksha bhava and Jaimini Ishta point to two complementary devotional paths. The Ishta (above) is primary; the D1 12th lord <b>{data.d1_twelfth.lord}</b> ({data.d1_twelfth.supporting_devata.deity.primary}) is a strong supporting practice.
                </p>
              )}
            </section>

            {/* === PALA + KULA DEVATAS === */}
            <section className="space-y-5">
              <div className="label-eyebrow text-muted flex items-center gap-2">
                <Sun size={14} weight="duotone" /> Supporting devatas in your Karakamsa wheel
              </div>
              <DevataCard
                role="pala"
                title="PALA DEVATA · daily protector"
                sub={`6th from Karakamsa · ${data.pala_devata.indicator_sign}`}
                dev={data.pala_devata}
              />
              <DevataCard
                role="kula"
                title="KULA DEVATA · family / dharma deity"
                sub={`9th from Karakamsa · ${data.kula_devata.indicator_sign}`}
                dev={data.kula_devata}
              />
            </section>
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Jaimini"
              pitch="Get a live walk-through of your Ishta Devata, your specific sadhana practice, and how to begin daily worship — with a Jaimini specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
